import { ENV } from '@/shared/config';
import { logger } from '@/shared/logger';
import { HTTP_STATUS, REQUEST_TIMEOUT_NONE } from '../consts';
import { ApiError, ApiErrorContext, ERROR_MESSAGES, isApiError } from './error';
import { createClientTraceContext } from './traceparent';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: unknown;
  params?: Record<string, unknown>;
  /** `null`이면 클라이언트 타임아웃 없음 (`X-Request-Timeout: none`) */
  timeout?: number | null;
  signal?: AbortSignal;
  /**
   * fetch keepalive — true이면 페이지 unload 후에도 요청 완료가 보장된다(브라우저 단).
   * 주로 BannerSyncCoordinator.flushImmediate처럼 새로고침/탭 닫기 직전 PUT에 사용.
   * keepalive 요청 body는 64KB 이하 권장.
   */
  keepalive?: boolean;
}

class ApiInstance {
  private baseURL: string;

  constructor({ baseURL }: { baseURL?: string }) {
    this.baseURL = baseURL || '';
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      params,
      timeout: timeoutOption,
      signal,
      keepalive,
    } = options;

    const timeout =
      timeoutOption === undefined ? ENV.API_TIMEOUT : timeoutOption;

    let url = `${this.baseURL}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    // same-origin(상대 경로) 요청에만 부착 — traceparent 는 CORS-safelisted 헤더가
    // 아니라 절대 URL(외부 오리진 가능)에 붙이면 preflight 를 유발한다
    const traceContext =
      url.startsWith('/') && !url.startsWith('//')
        ? createClientTraceContext()
        : undefined;

    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Request-Timeout':
        timeout === null ? REQUEST_TIMEOUT_NONE : String(timeout),
      ...(traceContext ? { traceparent: traceContext.traceparent } : {}),
    };

    // 요청 설정
    const requestOptions: RequestInit = {
      method,
      headers: { ...defaultHeaders, ...headers },
      signal,
      ...(keepalive ? { keepalive: true } : {}),
    };

    // Body 추가 (GET 요청이 아닌 경우)
    if (body && method !== 'GET') {
      if (body instanceof FormData) {
        delete (requestOptions.headers as Record<string, string>)[
          'Content-Type'
        ]; // FormData는 브라우저가 자동으로 설정
        requestOptions.body = body;
      } else {
        requestOptions.body = JSON.stringify(body);
      }
    }

    // 에러 발생 시 사용할 컨텍스트 정보 미리 정의
    const errorContext: ApiErrorContext = {
      url,
      method,
      params,
      // body가 너무 크거나 민감한 정보가 있다면 제외하거나 마스킹 처리 필요
      body: body instanceof FormData ? 'FormData' : body,
      ...(traceContext
        ? { trace_id: traceContext.trace_id, span_id: traceContext.span_id }
        : {}),
    };

    // AbortController를 사용한 타임아웃 처리
    const controller = new AbortController();
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      // 외부 signal이 있으면 연결
      if (signal) {
        signal.addEventListener('abort', () => controller.abort());
      }

      if (timeout !== null) {
        timeoutId = setTimeout(() => {
          if (ENV.IS_DEVELOPMENT) {
            console.warn(
              `[API Timeout] Request to ${url} timed out after ${timeout}ms`,
            );
          }
          controller.abort();
        }, timeout);
      }

      const response = await fetch(url, {
        ...requestOptions,
        signal: controller.signal,
      });

      // 응답 처리
      const responseData = await this.handleResponse<T>(response);

      return responseData;
    } catch (error) {
      return this.handleError(error as Error, errorContext);
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    if (!response.ok) {
      let errorMessage: string = ERROR_MESSAGES.UNKNOWN;
      let errorCode: string | undefined;

      if (isJson) {
        try {
          const errorResponse = await response.json();
          errorMessage =
            errorResponse.message || errorResponse.error || errorMessage;
          errorCode = errorResponse.code;
        } catch {
          throw new ApiError(ERROR_MESSAGES.UNKNOWN, response.status);
        }
      }

      throw new ApiError(errorMessage, response.status, errorCode);
    }

    // 성공 응답 처리
    if (response.status === HTTP_STATUS.NO_CONTENT) {
      return undefined as T;
    }

    if (isJson) {
      const json = await response.json();
      return { ...json, status: response.status } as T;
    }

    // JSON이 아닌 응답 (예: 파일 다운로드)
    const text = await response.text();
    return text as T;
  }

  /**
   * 내부 에러 처리
   */
  private handleError(error: Error, context?: ApiErrorContext): never {
    if (context) {
      const status = (error as ApiError).status;
      // 5xx·네트워크 오류만 error(클라에선 pod로 transmit), 4xx 등은 warn(노이즈 방지)
      const isServerFault = status === undefined || status >= 500;
      const level = isServerFault ? 'error' : 'warn';
      // 실패 이유는 구조화 필드(err/status)에도 실리지만, Kibana 리스트 뷰 등
      // message 컬럼만 보이는 곳에서 한눈에 읽히도록 라인에도 포함한다
      const reason =
        status === undefined
          ? `${error.name}: ${error.message}`
          : `${status} ${error.message}`;
      logger[level](
        {
          err: error,
          method: context.method,
          url: context.url,
          status,
          // 없을 때 undefined 로 넣으면 서버측 activeTraceFields 를 덮어쓰므로
          // 있을 때만 포함한다
          ...(context.trace_id
            ? { trace_id: context.trace_id, span_id: context.span_id }
            : {}),
        },
        `[API] ${context.method} ${context.url} failed (${reason})`,
      );
    }

    if (isApiError(error)) {
      error.context = context;
      throw error;
    }

    if (error.name === 'AbortError') {
      // 요청 취소는 보통 의도된 동작(이탈/타임아웃) → debug 로만 남김
      logger.debug({ url: context?.url }, '[API] request aborted');
      throw new ApiError(
        ERROR_MESSAGES.TIMEOUT,
        408, // Request Timeout
        'TIMEOUT',
        context,
      );
    }

    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new ApiError(
        ERROR_MESSAGES.NETWORK_ERROR,
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
        undefined,
        context,
      );
    }

    throw error;
  }

  public async get<T>(
    endpoint: string,
    options?: Omit<RequestOptions, 'method'>,
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  public async post<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  public async put<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  public async patch<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  public async delete<T>(
    endpoint: string,
    options?: Omit<RequestOptions, 'method'>,
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// 싱글톤 인스턴스 생성
export const apiInstance = new ApiInstance({});
